import React from "react";

type IconProps = React.SVGProps<SVGSVGElement> & { size?: number };

function Icon({
  size = 20,
  children,
  ...rest
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const ArrowIcon = ({ size = 14, ...rest }: IconProps) => (
  <Icon size={size} strokeWidth={2.4} {...rest}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Icon>
);

export const LockIcon = ({ size = 20, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </Icon>
);

export const CloudIcon = ({ size = 20, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M17 19a4 4 0 0 0 0-8 6 6 0 0 0-11.7 1.6A4 4 0 0 0 6 19h11z" />
  </Icon>
);

export const DesktopIcon = ({ size = 20, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <rect x="3" y="4" width="18" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </Icon>
);

export const BankIcon = ({ size = 20, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M3 10l9-6 9 6M5 10v9M19 10v9M9 10v9M15 10v9M3 21h18" />
  </Icon>
);

export const DownloadIcon = ({ size = 14, ...rest }: IconProps) => (
  <Icon size={size} strokeWidth={2.2} {...rest}>
    <path d="M12 4v12M6 12l6 6 6-6M5 21h14" />
  </Icon>
);

export const PlusIcon = ({ size = 14, ...rest }: IconProps) => (
  <Icon size={size} strokeWidth={2.4} {...rest}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const AppleGlyph = ({
  size = 18,
  ...rest
}: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg
    width={(size * 16) / 18}
    height={size}
    viewBox="0 0 16 18"
    fill="currentColor"
    {...rest}
  >
    <path d="M11.2 9.4c0-1.8 1.5-2.7 1.6-2.7-.9-1.3-2.2-1.5-2.7-1.5-1.1-.1-2.2.7-2.8.7-.6 0-1.5-.7-2.5-.6-1.3 0-2.5.7-3.1 1.9-1.4 2.3-.4 5.7 1 7.6.6.9 1.4 1.9 2.4 1.9.9 0 1.3-.6 2.5-.6 1.1 0 1.5.6 2.5.6 1 0 1.7-.9 2.3-1.9.7-1.1 1-2.1 1-2.2-.1 0-2.2-.8-2.2-3.2zM9.4 4c.5-.6.9-1.5.8-2.4-.8 0-1.7.5-2.2 1.1-.5.6-.9 1.5-.8 2.3.8.1 1.7-.4 2.2-1z" />
  </svg>
);

export const WindowsGlyph = ({
  size = 16,
  ...rest
}: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    {...rest}
  >
    <path d="M0 2l6.5-.9v6.4H0V2zm0 12V8.5h6.5v6.5L0 14zm7.5-6h8.5V0L7.5 1.2v6.8zm0 .5h8.5V16L7.5 14.7V8.5z" />
  </svg>
);

export const LinuxGlyph = ({
  size = 18,
  ...rest
}: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg
    width={(size * 16) / 18}
    height={size}
    viewBox="0 0 16 18"
    fill="currentColor"
    {...rest}
  >
    <path d="M8 0c-1.7 0-3 1.4-3 3.2 0 .5.1 1 .3 1.4-1 .9-1.6 2.2-1.7 3.7 0 .4-.3 1.5-1 2.3-.7.7-1.6 1.4-1.6 2.2 0 .8.7 1 1.4 1.2.6.1.9.4 1.1.8.7 1.4 2.6 3.2 4.5 3.2s3.8-1.8 4.5-3.2c.2-.4.5-.7 1.1-.8.7-.2 1.4-.4 1.4-1.2 0-.8-.9-1.5-1.6-2.2-.7-.8-1-1.9-1-2.3-.1-1.5-.7-2.8-1.7-3.7.2-.4.3-.9.3-1.4C11 1.4 9.7 0 8 0z" />
  </svg>
);

export const ChevronLeftIcon = ({ size = 16, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M15 6l-6 6 6 6" />
  </Icon>
);

export const ChevronRightIcon = ({ size = 16, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M9 6l6 6-6 6" />
  </Icon>
);

/** Disclosure chevron — rotates 180° when what it opens is open. */
export const ChevronDownIcon = ({ size = 16, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M6 9l6 6 6-6" />
  </Icon>
);

/** Arrows pointing outward to the sides — "fit everything in view". */
export const FitIcon = ({ size = 16, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M3 12h18M3 12l4-4M3 12l4 4M21 12l-4-4M21 12l-4 4" />
  </Icon>
);

export const TargetIcon = ({ size = 16, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

export const FullscreenIcon = ({ size = 16, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
  </Icon>
);

export const ExitFullscreenIcon = ({ size = 16, ...rest }: IconProps) => (
  <Icon size={size} {...rest}>
    <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
  </Icon>
);
